import type { Filter, FilterMatchMode } from "@/shared/components/ResourceView";
import type { SkillSummary } from "@/shared/domain/skills";

/** True for the "set complement" comparators (`NOT_EQUALS`, `NOT_CONTAINS`, …). */
function isNegative(comparator: string): boolean {
  return comparator.startsWith("NOT_");
}

/**
 * Evaluate a single string comparator against one haystack/needle pair. All
 * comparisons are case-insensitive, `EQUALS` included — a user typing a skill
 * name into the filter shouldn't have to match its casing.
 */
function stringComparatorMatches(
  haystack: string,
  comparator: string,
  needle: string,
): boolean {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  switch (comparator) {
    case "EQUALS":
      return h === n;
    case "NOT_EQUALS":
      return h !== n;
    case "CONTAINS":
    case "LIKE":
      return h.includes(n);
    case "NOT_CONTAINS":
    case "NOT_LIKE":
      return !h.includes(n);
    default:
      // Unknown comparator: don't drop the row over a filter we can't read.
      return true;
  }
}

function matchesStringFilter(
  haystack: string,
  comparator: string,
  values: readonly string[],
): boolean {
  const needles = values.filter((v) => v.trim().length > 0);
  if (needles.length === 0) {
    return true;
  }
  // Positive comparators fan out to OR across values, negative ones to AND
  // (set complement) — the same expansion `filterToCondition` applies when
  // building a server-side condition.
  return isNegative(comparator)
    ? needles.every((n) => stringComparatorMatches(haystack, comparator, n))
    : needles.some((n) => stringComparatorMatches(haystack, comparator, n));
}

function matchesTagFilter(
  tags: readonly string[],
  comparator: string,
  values: readonly string[],
): boolean {
  if (values.length === 0) {
    return true;
  }
  const owned = new Set(tags.map((t) => t.toLowerCase()));
  const anyOwned = values.some((v) => owned.has(v.toLowerCase()));
  // CONTAINS: the skill carries at least one of the selected tags.
  // NOT_CONTAINS: it carries none of them.
  return isNegative(comparator) ? !anyOwned : anyOwned;
}

function matchesFilter(skill: SkillSummary, filter: Filter): boolean {
  switch (filter.field) {
    case "name":
      return (
        filter.type === "string" &&
        matchesStringFilter(skill.skill.name, filter.comparator, filter.values)
      );
    case "created_by":
      return (
        filter.type === "string" &&
        matchesStringFilter(
          skill.skill.created_by,
          filter.comparator,
          filter.values,
        )
      );
    case "tags":
      return (
        filter.type === "set" &&
        matchesTagFilter(skill.skill.tags, filter.comparator, filter.values)
      );
    default:
      // A field reachable from the widget but unhandled here would silently
      // match every row — keep the field list in sync with FILTER_DEFINITIONS.
      return true;
  }
}

/**
 * Client-side evaluation of the DataTable filter widget's `Filter[]` output
 * against a single skill. The skills list is fetched in one shot, so the
 * standard server-side `filterToCondition` path doesn't apply — this is the
 * in-memory equivalent.
 *
 * `matchMode` joins distinct filters: `"AND"` (default) requires every
 * filter to match, `"OR"` requires at least one. An empty filter list
 * matches every skill.
 */
export function skillMatchesFilters(
  skill: SkillSummary,
  filters: readonly Filter[],
  matchMode: FilterMatchMode,
): boolean {
  if (filters.length === 0) {
    return true;
  }
  return matchMode === "OR"
    ? filters.some((f) => matchesFilter(skill, f))
    : filters.every((f) => matchesFilter(skill, f));
}
